import { Search, GitBranch, Network, Target, ChevronDown } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { KG_NODE_STYLES } from './kg-constants'

interface KgToolbarProps {
  layout: 'forceDirected' | 'hierarchical' | 'radial'
  onLayoutChange: (layout: 'forceDirected' | 'hierarchical' | 'radial') => void
  visibleTypes: Set<string>
  onTypeToggle: (type: string) => void
  onSetVisibleTypes: (types: Set<string>) => void
  availableTypes: string[]
  searchQuery: string
  onSearchChange: (q: string) => void
  onSearchSubmit: () => void
  depth: number
  onDepthChange: (d: number) => void
  nodeCount: number
  relCount: number
}

export function KgToolbar({
  layout,
  onLayoutChange,
  visibleTypes,
  onTypeToggle,
  onSetVisibleTypes,
  availableTypes,
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  depth,
  onDepthChange,
  nodeCount,
  relCount,
}: KgToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Layout toggle */}
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
        <button
          onClick={() => onLayoutChange('forceDirected')}
          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
            layout === 'forceDirected'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
          title="Force-directed layout"
        >
          <Network className="w-3.5 h-3.5 inline mr-1" />
          Force
        </button>
        <button
          onClick={() => onLayoutChange('hierarchical')}
          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
            layout === 'hierarchical'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
          title="Hierarchical layout"
        >
          <GitBranch className="w-3.5 h-3.5 inline mr-1" />
          Tree
        </button>
        <button
          onClick={() => onLayoutChange('radial')}
          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
            layout === 'radial'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
          title="Radial spiral layout"
        >
          <Target className="w-3.5 h-3.5 inline mr-1" />
          Radial
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onSearchSubmit()}
          placeholder="Search nodes..."
          className="pl-8 pr-3 py-1.5 text-xs w-44 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Depth slider */}
      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
        <span>Depth:</span>
        <input
          type="range"
          min={1}
          max={5}
          value={depth}
          onChange={e => onDepthChange(parseInt(e.target.value, 10))}
          className="w-16 h-1 accent-blue-500"
        />
        <span className="w-3 text-center font-medium">{depth}</span>
      </div>

      {/* Type filter */}
      <div className="relative" ref={filterRef}>
        <button
          onClick={() => setFilterOpen(!filterOpen)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Types ({visibleTypes.size}/{availableTypes.length})
          <ChevronDown className="w-3 h-3" />
        </button>
        {filterOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 p-2">
            <div className="flex gap-1 mb-2 border-b border-gray-200 dark:border-gray-700 pb-2">
              <button
                onClick={() => onSetVisibleTypes(new Set(availableTypes))}
                className="text-[10px] text-blue-500 hover:underline"
              >
                All
              </button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <button
                onClick={() => {
                  // Keep at least the first type so the graph never goes empty
                  const sorted = [...availableTypes].sort()
                  const first = sorted[0]
                  onSetVisibleTypes(new Set(first ? [first] : []))
                }}
                className="text-[10px] text-blue-500 hover:underline"
              >
                None
              </button>
            </div>
            {availableTypes.sort().map(type => {
              const style = KG_NODE_STYLES[type]
              return (
                <label key={type} className="flex items-center gap-2 px-1 py-0.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded text-xs">
                  <input
                    type="checkbox"
                    checked={visibleTypes.has(type)}
                    onChange={() => onTypeToggle(type)}
                    className="w-3 h-3 accent-blue-500"
                  />
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: style?.color || '#6b7280' }}
                  />
                  <span className="text-gray-700 dark:text-gray-300">{formatLabel(type)}</span>
                </label>
              )
            })}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 ml-auto">
        <span>{nodeCount} nodes</span>
        <span className="text-gray-300 dark:text-gray-600">&middot;</span>
        <span>{relCount} relationships</span>
      </div>
    </div>
  )
}

function formatLabel(label: string): string {
  return label.replace(/([A-Z])/g, ' $1').trim()
}
